import { Component, computed, effect, inject, TemplateRef, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { APP_ROUTES } from '@core/routes';
import { NavigationDirectionService } from '@core/services/navigation-direction.service';
import { RouterService } from '@core/services/router.service';
import { TranslationService } from '@core/translation.service';
import { isAuthenticated } from '@features/auth/store/auth.signals';
import { activeCustomer } from '@features/customer/store/work-with-customer.signals';
import { allBadge } from '@features/notifications/store/services/notification.store';
import { locale as toolbarLocale } from '@layout/toolbar/i18n/en';
import { PnycAvatarComponent } from '@ui-kit/pnyc-avatar/pnyc-avatar.component';
import { NavigationPanelItem, PnycNavigationPanelComponent } from '@ui-kit/pnyc-navigation-panel/pnyc-navigation-panel.component';
import { PnycPopupService } from '@ui-kit/popup/pnyc-popup.service';

type NavTab = 'search' | 'analytics' | 'customers' | 'notifications' | 'more';

const NAV_ROUTES: Partial<Record<NavTab, string>> = {
    search: APP_ROUTES.SEARCH,
    analytics: APP_ROUTES.ANALYTICS,
    notifications: APP_ROUTES.NOTIFICATIONS
};

const PAGE_TO_NAV: Record<string, NavTab> = {
    [APP_ROUTES.SEARCH]: 'search',
    [APP_ROUTES.ANALYTICS]: 'analytics',
    [APP_ROUTES.NOTIFICATIONS]: 'notifications',
    [APP_ROUTES.CUSTOMERS]: 'customers'
};

@Component({
    selector: 'navigation-panel',
    templateUrl: './navigation-panel.component.html',
    styleUrl: './navigation-panel.component.scss',
    imports: [PnycNavigationPanelComponent, PnycAvatarComponent]
})
export class NavigationPanelComponent {
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly navDirection = inject(NavigationDirectionService);
    private readonly popupService = inject(PnycPopupService);
    private readonly translationService = inject(TranslationService);

    public readonly activeCustomer = activeCustomer;

    private readonly panel = viewChild(PnycNavigationPanelComponent);
    private readonly activeCustomerTemplate = viewChild<TemplateRef<void>>('activeCustomerTemplate');

    constructor() {
        this.translationService.loadTranslations(toolbarLocale);

        effect(() => {
            const page = this.routerService.currentPage();
            const navId = page ? PAGE_TO_NAV[page] ?? null : null;

            this.panel()?.setActiveItem(navId);
        });
    }

    public readonly items = computed<NavigationPanelItem[]>(() => {
        if (!isAuthenticated()) {
            return [{ id: 'login', icon: 'log-in', name: 'Log In' }];
        }

        const customer = activeCustomer();

        return [
            { id: 'search', icon: 'search', name: 'Search' },
            // { id: 'analytics', icon: 'chart-column', name: this.translationService.instant('TOOLBAR.TITLES.ANALYTICS') },
            { id: 'customers', icon: 'users', name: 'Customers', template: customer ? this.activeCustomerTemplate() : undefined },
            { id: 'notifications', icon: 'bell', badge: allBadge(), name: 'Notifications' },
            { id: 'more', icon: 'kebab-horizontal', name: 'More' }
        ];
    });

    public onItemClicked(item: NavigationPanelItem): void {
        if (item.id === 'login') {
            this.router.navigate(['/']);

            return;
        }

        const index = this.items().findIndex(i => i.id === item.id);

        this.navDirection.setIndex(index);

        if (item.id === 'customers') {
            this.openCustomerPanel();

            return;
        }

        if (item.id === 'more') {
            this.openMorePanel();

            return;
        }

        const route = NAV_ROUTES[item.id as NavTab];

        if (route) {
            this.router.navigate([route]);
        }
    }

    private openCustomerPanel(): void {
        void import('@features/customer/components/customer-panel/customer-panel.component')
            .then(({ CustomerPanelComponent }) => this.popupService.openPanel(CustomerPanelComponent, null, { hasBackdrop: true, customClass: ['customer-panel-modal', 'pnyc-bottomsheet'] }, 'bottom'));
    }

    private openMorePanel(): void {
        void import('@layout/more-panel/more-panel.component')
            .then(({ MorePanelComponent }) => this.popupService.openPanel(MorePanelComponent, null, { hasBackdrop: true, customClass: ['more-panel-modal', 'pnyc-bottomsheet'] }, 'bottom'));
    }
}
